import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import {
  ClipboardList,
  Download,
  Eye,
  Loader2,
  Pencil,
  Upload,
  X,
  RotateCcw,
} from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { payrollOpsService, payrollService } from "../../../services/api";
import { formatInr, formatPayMonthLabel } from "./runPayrollUtils";
import TdsComputationBreakdownModal from "./TdsComputationBreakdownModal";
import OneTimeTaxModal from "./OneTimeTaxModal";
const TaxPlanningModal = lazy(() => import("../tax-planning/TaxPlanningModal"));
import { usePermissions } from "../../../rbac/PermissionsContext";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";

const TDS_OVERRIDE_REASONS = [
  "Mid-year revision",
  "Bonus / one-time adjustment",
  "Previous employer TDS",
  "Manual correction",
  "Other",
];

function financialYearFromPayMonth(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return "";
  const y = Number(m[1]);
  const month = Number(m[2]);
  if (month >= 4) return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  return `${y - 1}-${String(y % 100).padStart(2, "0")}`;
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/[*]/g, "")
    .replace(/\s+/g, "");
}

const DECLARATION_COLUMNS = [
  { key: "empCode", label: "Code" },
  { key: "name", label: "Name" },
  { key: "taxRegime", label: "Regime" },
  { key: "status", label: "Status" },
  { key: "financialYear", label: "FY" },
  { key: "computedTds", label: "TDS Computed", format: "inr" },
];

const TDS_COLUMNS = [
  { key: "empCode", label: "Code" },
  { key: "name", label: "Name" },
  { key: "taxRegime", label: "Regime" },
  { key: "previousPayMonth", label: "Prev month" },
  { key: "previousTds", label: "Prev TDS", format: "inr" },
  { key: "computedTds", label: "Computed", format: "inr" },
  { key: "actualTds", label: "Applied", format: "inr" },
  { key: "delta", label: "Δ", format: "inr" },
  { key: "reason", label: "Reason" },
];

function cellValue(row, col) {
  const v = row[col.key];
  if (col.format === "inr") {
    if (v == null || v === "") return "—";
    if (col.key === "actualTds" && row.isOverridden) {
      return `${formatInr(v)} *`;
    }
    return formatInr(v);
  }
  return v ?? "—";
}

function OverrideModal({
  row,
  payMonth,
  darkMode,
  onClose,
  onSaved,
  locked = false,
  allowProcessed = false,
}) {
  const [amount, setAmount] = useState(String(row.actualTds ?? row.tdsAmount ?? row.computedTds ?? ""));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = locked && !allowProcessed;

  const submit = async () => {
    if (blocked) {
      toast.error("Unprocess this employee before overriding TDS");
      return;
    }
    const overrideTds = Number(amount);
    if (!Number.isFinite(overrideTds) || overrideTds < 0) {
      toast.error("Enter a valid TDS amount");
      return;
    }
    setBusy(true);
    try {
      await payrollOpsService.overrideTds(row.employeeId, {
        payMonth,
        overrideTds,
        reason,
        allowProcessed: !!allowProcessed,
      });
      toast.success(
        allowProcessed
          ? "TDS updated on processed payslip"
          : "TDS override saved — remaining FY months will adjust"
      );
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save override");
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async () => {
    if (blocked) {
      toast.error("Unprocess this employee before clearing TDS override");
      return;
    }
    setBusy(true);
    try {
      await payrollOpsService.clearTdsOverride(row.employeeId, payMonth, {
        allowProcessed: !!allowProcessed,
      });
      toast.success("Override cleared — computed TDS restored");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to clear override");
    } finally {
      setBusy(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-md rounded-xl border shadow-xl ${
          darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))] text-white" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={`flex items-center justify-between border-b px-4 py-3 ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"}`}>
          <h3 className="text-sm font-semibold">Override TDS — {row.name}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:opacity-70">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          {blocked ? (
            <p
              className={`rounded border px-3 py-2 text-xs ${
                darkMode
                  ? "border-amber-800/50 bg-amber-950/30 text-amber-100"
                  : "border-amber-200 bg-amber-50 text-amber-950"
              }`}
            >
              This employee is already processed for {payMonth}. Unprocess them in Process Payroll
              before overriding TDS.
            </p>
          ) : allowProcessed ? (
            <p
              className={`rounded border px-3 py-2 text-xs ${
                darkMode
                  ? "border-sky-800/40 bg-sky-950/30 text-sky-100"
                  : "border-sky-200 bg-sky-50 text-sky-950"
              }`}
            >
              Editing TDS on the processed payslip for {payMonth}. Net pay updates immediately.
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="opacity-60">Computed</span>
              <p className="text-right font-semibold tabular-nums">{formatInr(row.computedTds)}</p>
            </div>
            <div>
              <span className="opacity-60">Previous month</span>
              <p className="text-right font-semibold tabular-nums">{formatInr(row.previousTds)}</p>
            </div>
            <div>
              <span className="opacity-60">Annual tax</span>
              <p className="text-right font-semibold tabular-nums">{formatInr(row.annualTaxLiability)}</p>
            </div>
            <div>
              <span className="opacity-60">Balance after month</span>
              <p className="text-right font-semibold tabular-nums">{formatInr(row.balanceTaxAfterMonth)}</p>
            </div>
          </div>
          <p className="text-xs opacity-70">
            Override applies to this payroll month. Future months spread the remaining FY tax over
            {row.remainingMonths != null ? ` ${row.remainingMonths} month(s)` : " remaining months"}.
          </p>
          <label className="block">
            <span className="text-xs font-medium">TDS amount</span>
            <input
              type="number"
              min="0"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={blocked || busy}
              className={`mt-1 w-full rounded border px-3 py-2 text-sm disabled:opacity-50 ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))]"
              }`}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium">Reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              disabled={blocked || busy}
              className={`mt-1 w-full rounded border px-3 py-2 text-sm disabled:opacity-50 ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]" : "border-[hsl(var(--ds-border))]"
              }`}
              placeholder="e.g. Mid-year revision, bonus adjustment"
            />
          </label>
        </div>
        <div className={`flex justify-between gap-2 border-t px-4 py-3 ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"}`}>
          {(row.isOverridden || row.isOverridden === true) && !blocked ? (
            <button
              type="button"
              onClick={clearOverride}
              disabled={busy}
              className="flex items-center gap-1 text-xs text-amber-600 hover:underline disabled:opacity-50"
            >
              <RotateCcw size={14} /> Clear override
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-xs">
              {blocked ? "Close" : "Cancel"}
            </button>
            {!blocked ? (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs text-white disabled:opacity-50"
            >
              Save override
            </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function compareSortValues(a, b, key) {
  const va = a?.[key];
  const vb = b?.[key];
  const na = Number(va);
  const nb = Number(vb);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(va).trim() !== "" && String(vb).trim() !== "") {
    return na - nb;
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function DataTable({
  columns,
  rows,
  darkMode,
  onOverride,
  onViewComputation,
  onViewTaxPlanner,
  isEmployeeProcessed,
  emptyMessage,
  search = "",
  onSearchChange,
  hideSearchBar = false,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [localSearch, setLocalSearch] = useState("");
  const searchValue = onSearchChange ? search : localSearch;
  const setSearch = onSearchChange || setLocalSearch;

  const sourceRows = Array.isArray(rows) ? rows : [];
  const filteredRows = useMemo(
    () => filterRowsByEmpCodeOrName(sourceRows, searchValue),
    [sourceRows, searchValue]
  );

  const sortedRows = useMemo(() => {
    const list = [...filteredRows];
    if (!sortKey) return list;
    list.sort((a, b) => {
      const cmp = compareSortValues(a, b, sortKey);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [filteredRows, sortKey, sortDir]);

  if (!sourceRows.length) {
    return (
      <p className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
        darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
      }`}>
        {emptyMessage || "No records to display."}
      </p>
    );
  }

  const showActions = onOverride || onViewComputation || onViewTaxPlanner;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {!hideSearchBar ? (
        <div className="shrink-0">
          <EmpCodeNameSearchBar
            value={searchValue}
            onChange={setSearch}
            darkMode={darkMode}
            showCount
            total={sourceRows.length}
            filtered={sortedRows.length}
            className="!flex-none"
          />
        </div>
      ) : null}
      {!sortedRows.length ? (
        <p className={`rounded-lg border border-dashed px-4 py-8 text-center text-sm ${
          darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
        }`}>
          No employees match “{String(searchValue || "").trim()}”.
        </p>
      ) : (
    <div
      className={`h-0 min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg border text-xs ${
        darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
      }`}
    >
      <table className="w-full min-w-[640px]">
        <thead className={`sticky top-0 z-10 ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
          <tr>
            <th className="px-2 py-2 text-left font-bold whitespace-nowrap">Sl. No</th>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`px-2 py-2 font-bold whitespace-nowrap ${
                  c.format === "inr" ? "text-right" : "text-left"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(c.key)}
                  className={`font-bold hover:text-[hsl(var(--ds-primary))] ${
                    c.format === "inr" ? "w-full text-right" : "text-left"
                  } ${sortKey === c.key ? "text-[hsl(var(--ds-primary))]" : ""}`}
                  title={`Sort by ${c.label}`}
                >
                  {c.label}
                </button>
              </th>
            ))}
            {showActions && <th className="px-2 py-2 text-left font-bold">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr
              key={`${row.employeeId ?? "row"}-${i}`}
              className={`border-t ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"} ${
                row.hasDiscrepancy ? (darkMode ? "bg-amber-950/20" : "bg-amber-50/60") : ""
              }`}
            >
              <td className="px-2 py-1.5 tabular-nums text-[hsl(var(--ds-muted-fg))]">{i + 1}</td>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-2 py-1.5 tabular-nums ${c.format === "inr" ? "text-right" : "text-left"}`}
                >
                  {cellValue(row, c)}
                </td>
              ))}
              {showActions && (
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    {onViewComputation ? (
                      <button
                        type="button"
                        onClick={() => onViewComputation(row)}
                        className={`inline-flex items-center gap-1 rounded p-1 text-[11px] ${
                          darkMode
                            ? "text-red-400 hover:bg-[hsl(var(--ds-muted))]"
                            : "text-[hsl(var(--ds-primary))] hover:bg-red-50"
                        }`}
                        title="View TDS computation breakdown"
                        aria-label="View TDS computation"
                      >
                        <Eye size={14} />
                      </button>
                    ) : null}
                    {onViewTaxPlanner ? (
                      <button
                        type="button"
                        onClick={() => onViewTaxPlanner(row)}
                        className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium ${
                          darkMode
                            ? "text-emerald-400 hover:bg-[hsl(var(--ds-muted))]"
                            : "text-emerald-700 hover:bg-emerald-50"
                        }`}
                        title="View Tax Planning form"
                        aria-label="View Tax Planning form"
                      >
                        <ClipboardList size={14} />
                        <span className="hidden sm:inline">Tax Planning</span>
                      </button>
                    ) : null}
                    {onOverride ? (
                      (() => {
                        const empId = Number(row.employeeId ?? row.id);
                        const processed =
                          typeof isEmployeeProcessed === "function"
                            ? !!isEmployeeProcessed(empId)
                            : false;
                        if (processed) {
                          return (
                            <span className="group relative inline-flex">
                              <button
                                type="button"
                                disabled
                                className={`inline-flex cursor-not-allowed items-center rounded p-1 text-[11px] opacity-40 ${
                                  darkMode ? "text-blue-400" : "text-blue-600"
                                }`}
                                aria-label="Unprocess to edit"
                              >
                                <Pencil size={14} />
                              </button>
                              <span
                                role="tooltip"
                                className={`pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md px-2 py-1 text-[10px] font-medium opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100 ${
                                  darkMode
                                    ? "bg-[hsl(var(--ds-fg))] text-[hsl(var(--ds-surface))]"
                                    : "bg-[hsl(var(--ds-fg))] text-[hsl(var(--ds-surface))]"
                                }`}
                              >
                                Unprocess to edit
                              </span>
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => onOverride(row)}
                            className={`inline-flex items-center rounded p-1 text-[11px] ${
                              darkMode
                                ? "text-blue-400 hover:bg-[hsl(var(--ds-muted))]"
                                : "text-blue-600 hover:bg-blue-50"
                            }`}
                            title="Edit applied TDS for this month"
                            aria-label="Edit TDS"
                          >
                            <Pencil size={14} />
                          </button>
                        );
                      })()
                    ) : null}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Spacer so last rows are not clipped under sticky footer / Next bar */}
      <div className="h-10 shrink-0" aria-hidden />
    </div>
      )}
    </div>
  );
}

function resolveDeclarationRows(result) {
  if (!result) return [];
  if (Array.isArray(result.rows) && result.rows.length) return result.rows;
  const section = result.sections?.find((s) => s.title === "Declarations");
  if (section?.rows?.length) return section.rows;
  const byRegime = result.meta?.employeesByRegime;
  if (byRegime) {
    return [
      ...(byRegime.OLD || []),
      ...(byRegime.NEW || []),
      ...(byRegime.MISSING || []),
    ];
  }
  return [];
}

function resolveTdsRows(result) {
  if (!result) return [];
  if (result.meta?.tdsRows?.length) return result.meta.tdsRows;
  const section = result.sections?.find((s) => s.title === "TDS comparison");
  return section?.rows || [];
}

export default function PayrollDeclarationsTdsPanel({
  result,
  payMonth,
  darkMode = false,
  loading = false,
  showAllTds = false,
  onToggleShowAll,
  onRefresh,
  onRunCheck,
  processedIdSet = null,
}) {
  const [activeTab, setActiveTab] = useState("tds");
  const [tableSearch, setTableSearch] = useState("");
  const [overrideRow, setOverrideRow] = useState(null);
  const [computationRow, setComputationRow] = useState(null);
  const [taxPlannerEmployee, setTaxPlannerEmployee] = useState(null);
  const [oneTimeTaxOpen, setOneTimeTaxOpen] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const tdsFileRef = useRef(null);
  const declFileRef = useRef(null);
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canViewTdsDetail =
    isAdmin ||
    hasScreenAccess("payroll", "pay_proc_process", "view") ||
    hasScreenAccess("payroll", "run_payroll", "view");
  const canEditPayroll =
    isAdmin ||
    hasScreenAccess("payroll", "pay_proc_process", "edit") ||
    hasScreenAccess("payroll", "pay_proc_process", "create");
  const isEmployeeProcessed = useCallback(
    (employeeId) => {
      if (!processedIdSet || typeof processedIdSet.has !== "function") return false;
      return processedIdSet.has(Number(employeeId));
    },
    [processedIdSet]
  );

  const openOverride = useCallback(
    (row) => {
      const empId = Number(row?.employeeId ?? row?.id);
      if (isEmployeeProcessed(empId)) return;
      setOverrideRow(row);
    },
    [isEmployeeProcessed]
  );
  const canViewTaxPlanner =
    isAdmin ||
    hasScreenAccess("payroll", "tax_declaration", "view") ||
    hasScreenAccess("payroll", "run_payroll", "view");

  const summary = result?.summary || {};
  const meta = result?.meta || {};
  const tdsRowsAll = resolveTdsRows(result);
  const tdsByEmployeeId = useMemo(() => {
    const map = new Map();
    tdsRowsAll.forEach((r) => {
      const id = Number(r.employeeId ?? r.id);
      if (id > 0) map.set(id, r);
    });
    return map;
  }, [tdsRowsAll]);

  const uniqueDeclarations = useMemo(() => {
    return resolveDeclarationRows(result).map((row) => {
      const id = Number(row.employeeId ?? row.id);
      const tds = tdsByEmployeeId.get(id);
      return {
        ...row,
        computedTds:
          tds?.computedTds != null
            ? tds.computedTds
            : row.computedTds != null
              ? row.computedTds
              : null,
      };
    });
  }, [result, tdsByEmployeeId]);

  // Default: discrepancies only. Checkbox "Show all employees" expands the list.
  const tdsRows = showAllTds
    ? tdsRowsAll
    : tdsRowsAll.filter((r) => r.hasDiscrepancy === true);
  const discrepancyCount =
    summary.tdsDiscrepancyCount ?? tdsRowsAll.filter((r) => r.hasDiscrepancy === true).length;
  const tdsError = meta.tdsError;
  const fyLabel = summary.financialYear || financialYearFromPayMonth(payMonth);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await payrollService.getPayrollSettings();
        if (!cancelled) setPayrollSettings(res?.data || res || null);
      } catch {
        /* non-blocking — modal still opens with safe defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openTaxPlanner = (row) => {
    const empId = Number(row?.employeeId ?? row?.id);
    if (!empId) {
      toast.error("Employee id missing for this row");
      return;
    }
    setTaxPlannerEmployee({
      id: empId,
      employeeId: empId,
      empCode: row.empCode || row.employeeCode || row.employeeId,
      name:
        row.name ||
        row.employeeName ||
        [row.firstName, row.lastName].filter(Boolean).join(" ") ||
        "—",
      taxRegime: row.taxRegime,
    });
  };

  const taxPlannerAction = canViewTaxPlanner ? openTaxPlanner : null;
  const tabs = [
    {
      id: "tds",
      label: "TDS Comparison",
      count: showAllTds ? tdsRowsAll.length : discrepancyCount,
      badge: showAllTds ? discrepancyCount : 0,
    },
    { id: "declarations", label: "Declarations", count: uniqueDeclarations.length },
  ];

  useEffect(() => {
    setTableSearch("");
  }, [activeTab]);

  const toolbarBtn = `inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ${
    darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
  }`;

  const downloadDeclarationsGridExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("New Joinee Declarations", {
        views: [{ state: "frozen", ySplit: 2 }],
      });
      ws.mergeCells(1, 1, 1, 8);
      const title = ws.getCell(1, 1);
      title.value = `New Joinee Declarations · ${formatPayMonthLabel(payMonth)} (${payMonth}) · FY ${fyLabel || "—"}`;
      title.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      title.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F766E" } };
      const headers = [
        "Sl. No",
        "Code",
        "Name",
        "Regime",
        "Status",
        "FY",
        "TDS Computed",
        "Pay Month",
      ];
      headers.forEach((h, i) => {
        const cell = ws.getCell(2, i + 1);
        cell.value = h;
        cell.font = { bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5E7EB" } };
      });
      uniqueDeclarations.forEach((row, idx) => {
        const r = idx + 3;
        ws.getCell(r, 1).value = idx + 1;
        ws.getCell(r, 2).value = row.empCode || "";
        ws.getCell(r, 3).value = row.name || "";
        ws.getCell(r, 4).value = row.taxRegime || "";
        ws.getCell(r, 5).value = row.status || "";
        ws.getCell(r, 6).value = row.financialYear || fyLabel || "";
        const tds = Number(row.computedTds);
        ws.getCell(r, 7).value = Number.isFinite(tds) ? tds : null;
        ws.getCell(r, 7).numFmt = "#,##0.00";
        ws.getCell(r, 8).value = payMonth || "";
      });
      [8, 12, 24, 10, 12, 10, 14, 12].forEach((w, i) => {
        ws.getColumn(i + 1).width = w;
      });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Declarations_TDS_${payMonth || "month"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Declarations export downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  const downloadTdsComputationExport = async () => {
    if (!showAllTds) {
      toast.error("Enable “Show all employees” to export the full TDS computation workbook");
      return;
    }
    if (!payMonth) {
      toast.error("Pay month is required");
      return;
    }
    const monthLabel = formatPayMonthLabel(payMonth);
    const ok = window.confirm(
      `TDS Comparison lists employees for pay month ${monthLabel} (${payMonth}) only.\n\n` +
        `Export will generate the TDS Computation workbook for the entire FY ${fyLabel || "—"} ` +
        `(Active + Joined + Relieved in that FY), not only the employees on this month’s comparison grid.\n\n` +
        `Tax amounts use ${monthLabel} as the as-of month.\n\nContinue?`
    );
    if (!ok) return;

    setExportBusy(true);
    try {
      const res = await payrollOpsService.exportTdsComputationWorkbook({
        payMonth,
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      a.download =
        match?.[1] ||
        `TDS_Computation_FY${String(fyLabel || "").replace("/", "-")}_AllEmployees.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      const n = res.headers?.["x-tds-export-employees"];
      toast.success(
        n
          ? `TDS computation export ready (${n} employee(s) for FY ${fyLabel || "—"})`
          : `TDS computation export ready for FY ${fyLabel || "—"}`
      );
    } catch (e) {
      let msg = e?.message || "Export failed";
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) msg = parsed.message;
        } else if (e?.response?.data?.message) {
          msg = e.response.data.message;
        }
      } catch (_ignore) {
        /* keep msg */
      }
      toast.error(msg);
    } finally {
      setExportBusy(false);
    }
  };

  const downloadTdsOverrideTemplate = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const instr = wb.addWorksheet("Instructions");
      instr.getColumn(1).width = 88;
      [
        "TDS Override Bulk Import — Instructions",
        "",
        "1. Use the TDS Overrides sheet. Do not rename header columns.",
        "2. Required: Emp Code, Financial Year, Pay Month, Override TDS (≥ 0).",
        "3. Financial Year and Pay Month must match the current Run Payroll month.",
        `4. Current context: FY ${fyLabel || "—"} · Pay Month ${formatPayMonthLabel(payMonth)} (${payMonth}).`,
        "5. Remarks/Reason — select from the dropdown (or type Other).",
        "6. Name is optional (for reference only).",
        "7. Upload from Run Payroll → TDS Review / Audit → TDS Comparison → Import Excel.",
        "8. Each row applies an override for that employee for this pay month; remaining FY months adjust.",
      ].forEach((line, idx) => {
        const cell = instr.getCell(idx + 1, 1);
        cell.value = line;
        if (idx === 0) cell.font = { bold: true, size: 13 };
      });

      const lists = wb.addWorksheet("Lists");
      const fyOptions = [];
      const baseY = Number(String(fyLabel).slice(0, 4)) || new Date().getFullYear();
      for (let i = -1; i <= 1; i += 1) {
        const y = baseY + i;
        fyOptions.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
      }
      fyOptions.forEach((fy, i) => {
        lists.getCell(i + 1, 1).value = fy;
      });
      lists.getCell(1, 2).value = formatPayMonthLabel(payMonth);
      lists.getCell(2, 2).value = payMonth;
      TDS_OVERRIDE_REASONS.forEach((r, i) => {
        lists.getCell(i + 1, 3).value = r;
      });
      lists.state = "veryHidden";

      const ws = wb.addWorksheet("TDS Overrides");
      const headers = [
        "Emp Code",
        "Name",
        "Financial Year",
        "Pay Month",
        "Override TDS",
        "Remarks / Reason",
      ];
      ws.addRow(headers);
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8E8E8" },
        };
      });
      ws.addRow([
        "",
        "",
        fyLabel || fyOptions[0],
        formatPayMonthLabel(payMonth),
        "",
        TDS_OVERRIDE_REASONS[0],
      ]);
      ws.columns = [
        { width: 14 },
        { width: 28 },
        { width: 16 },
        { width: 16 },
        { width: 14 },
        { width: 28 },
      ];

      ws.dataValidations.add("C2:C2000", {
        type: "list",
        allowBlank: false,
        formulae: [`Lists!$A$1:$A$${fyOptions.length}`],
        showErrorMessage: true,
        errorTitle: "Financial Year",
        error: "Select a financial year from the dropdown",
      });
      ws.dataValidations.add("D2:D2000", {
        type: "list",
        allowBlank: false,
        formulae: ["Lists!$B$1:$B$2"],
        showErrorMessage: true,
        errorTitle: "Pay Month",
        error: "Select the current pay month",
      });
      ws.dataValidations.add("F2:F2000", {
        type: "list",
        allowBlank: true,
        formulae: [`Lists!$C$1:$C$${TDS_OVERRIDE_REASONS.length}`],
        showErrorMessage: true,
        errorTitle: "Remarks / Reason",
        error: "Select a reason from the dropdown",
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tds-overrides-${payMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("TDS override template downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to build template");
    }
  };

  const parseTdsOverrideRows = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName =
      workbook.SheetNames.find((n) => /tds|override/i.test(n)) ||
      workbook.SheetNames.find((n) => !/instruction|list/i.test(n)) ||
      workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
    return (json || []).map((raw) => {
      const mapped = {};
      Object.entries(raw).forEach(([k, v]) => {
        mapped[normalizeHeader(k)] = v;
      });
      return {
        empCode:
          mapped.empcode ||
          mapped.employeecode ||
          mapped.code ||
          "",
        name: mapped.name || mapped.employeename || "",
        financialYear: mapped.financialyear || mapped.fy || "",
        payMonth: mapped.paymonth || mapped.month || payMonth,
        overrideTds: mapped.overridetds || mapped.overridetdsamount || mapped.tds || "",
        reason:
          mapped["remarks/reason"] ||
          mapped.remarksreason ||
          mapped.remarks ||
          mapped.reason ||
          "",
      };
    });
  };

  const handleTdsImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canEditPayroll) {
      toast.error("You do not have permission to import TDS overrides");
      e.target.value = "";
      return;
    }
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xls") && !lower.endsWith(".xlsx")) {
      toast.error("Only Excel files (.xls / .xlsx) are allowed");
      e.target.value = "";
      return;
    }
    setImportBusy(true);
    try {
      const parsed = await parseTdsOverrideRows(file);
      const dataRows = parsed.filter(
        (r) => String(r.empCode || "").trim() || String(r.overrideTds ?? "").trim()
      );
      if (!dataRows.length) {
        toast.error("No data rows in file");
        return;
      }
      const res = await payrollOpsService.bulkOverrideTds({
        payMonth,
        rows: dataRows,
      });
      if (res.errors?.length) {
        toast.error(
          res.message ||
            `${res.updated || 0} saved, ${res.errors.length} error(s). Processed employees must be unprocessed first.`
        );
        console.warn("TDS override import errors", res.errors);
      } else {
        toast.success(res.message || `${res.updated} override(s) saved`);
      }
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setImportBusy(false);
      if (tdsFileRef.current) tdsFileRef.current.value = "";
    }
  };

  const downloadDeclarationTemplate = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const currentFy = fyLabel || financialYearFromPayMonth(payMonth);
      const fyOptions = [];
      const baseY = Number(String(currentFy).slice(0, 4)) || new Date().getFullYear();
      for (let i = -2; i <= 1; i += 1) {
        const y = baseY + i;
        fyOptions.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
      }

      const headers = [
        "Sr. No",
        "Employee Code",
        "Employee Name",
        "Financial Year",
        "Tax Regime",
        "Status",
        "Prev Employer Name",
        "Prev Gross Salary",
        "Prev Tax Deducted",
        "HRA1 - City Type",
        "HRA1 - Total Rent Paid",
        "HRA1 - Landlord PAN",
        "HRA1 - Address",
        "80C - Life Insurance Premium",
        "80C - PPF Contribution",
        "80C - VPF Contribution",
        "80C - Pension Fund",
        "80C - NSC Investment",
        "80C - NSC Interest Reinvested",
        "80C - ULIP Investment",
        "80C - ELSS Investment",
        "80C - Tuition Fees",
        "80C - Principal Housing Loan",
        "80C - Registration Charges",
        "80C - Sukanya Samriddhi",
        "80C - Infrastructure Bonds",
        "80C - Bank Fixed Deposit",
        "VIA - Self Medical Insurance",
        "VIA - Parents Medical Insurance",
        "VIA - Senior Citizen Parents Medical",
        "VIA - Preventive Health Checkup",
        "VIA - Education Loan Interest",
        "VIA - NPS Additional Contribution",
        "VIA - Home Loan Interest (80EE)",
        "VIA - Home Loan Interest (80EEA)",
        "VIA - Home Loan Interest (80EEB)",
        "VIA - Donations (80G)",
        "VIA - Savings Interest (80TTA)",
        "VIA - Deposit Interest (80TTB)",
        "HouseProp1 - Occupancy",
        "HouseProp1 - Loan Before Apr 1999",
        "HouseProp1 - Interest Paid",
        "HouseProp1 - Address",
        "Remarks",
      ];

      const instr = wb.addWorksheet("Instructions");
      instr.getColumn(1).width = 92;
      [
        "Investment Declaration Bulk Import — Instructions",
        "",
        "1. Use the Declarations sheet. Do not rename header columns.",
        "2. Required: Employee Code, Financial Year, Tax Regime (OLD or NEW).",
        "3. OLD regime: fill HRA / 80C / VIA / House Property amounts (annual figures).",
        "4. NEW regime: set Tax Regime = NEW; deduction columns may be left blank.",
        `5. Current payroll context: FY ${currentFy || "—"} · Pay Month ${formatPayMonthLabel(payMonth)}.`,
        "6. Status defaults to SUBMITTED if blank (use DRAFT to keep editable).",
        "7. City Type: Metro / Non-metro. Occupancy: SELF_OCCUPIED / LET_OUT. Loan Before Apr 1999: Yes / No.",
        "8. Re-importing the same Employee Code + FY upserts (overwrites) the declaration.",
      ].forEach((line, idx) => {
        const cell = instr.getCell(idx + 1, 1);
        cell.value = line;
        if (idx === 0) cell.font = { bold: true, size: 13 };
      });

      const lists = wb.addWorksheet("Lists");
      fyOptions.forEach((fy, i) => {
        lists.getCell(i + 1, 1).value = fy;
      });
      ["OLD", "NEW"].forEach((v, i) => {
        lists.getCell(i + 1, 2).value = v;
      });
      ["DRAFT", "SUBMITTED", "REVIEWED", "APPROVED"].forEach((v, i) => {
        lists.getCell(i + 1, 3).value = v;
      });
      ["Metro", "Non-metro"].forEach((v, i) => {
        lists.getCell(i + 1, 4).value = v;
      });
      ["SELF_OCCUPIED", "LET_OUT"].forEach((v, i) => {
        lists.getCell(i + 1, 5).value = v;
      });
      ["Yes", "No"].forEach((v, i) => {
        lists.getCell(i + 1, 6).value = v;
      });
      lists.state = "veryHidden";

      const ws = wb.addWorksheet("Declarations");
      ws.addRow(headers);
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8E8E8" },
        };
      });

      const sample = Object.fromEntries(headers.map((h) => [h, ""]));
      sample["Sr. No"] = 1;
      sample["Employee Code"] = "EMP001";
      sample["Employee Name"] = "Sample Employee";
      sample["Financial Year"] = currentFy || fyOptions[0];
      sample["Tax Regime"] = "OLD";
      sample["Status"] = "SUBMITTED";
      sample["Prev Employer Name"] = "Not Applicable";
      sample["Prev Gross Salary"] = 0;
      sample["Prev Tax Deducted"] = 0;
      sample["HRA1 - City Type"] = "Metro";
      sample["HRA1 - Total Rent Paid"] = 240000;
      sample["80C - Life Insurance Premium"] = 30000;
      sample["80C - PPF Contribution"] = 40000;
      sample["HouseProp1 - Occupancy"] = "SELF_OCCUPIED";
      sample["HouseProp1 - Loan Before Apr 1999"] = "No";
      sample["Remarks"] =
        "Fill Old-regime amounts when Tax Regime = OLD. Leave blank for NEW.";
      ws.addRow(headers.map((h) => sample[h]));

      headers.forEach((_, i) => {
        ws.getColumn(i + 1).width = i === 2 || i === headers.length - 1 ? 28 : 18;
      });

      const lastData = 500;
      ws.dataValidations.add(`D2:D${lastData}`, {
        type: "list",
        allowBlank: false,
        formulae: [`Lists!$A$1:$A$${fyOptions.length}`],
        showErrorMessage: true,
        errorTitle: "Financial Year",
        error: "Select a financial year from the dropdown",
      });
      ws.dataValidations.add(`E2:E${lastData}`, {
        type: "list",
        allowBlank: false,
        formulae: ["Lists!$B$1:$B$2"],
        showErrorMessage: true,
        errorTitle: "Tax Regime",
        error: "Select OLD or NEW",
      });
      ws.dataValidations.add(`F2:F${lastData}`, {
        type: "list",
        allowBlank: true,
        formulae: ["Lists!$C$1:$C$4"],
      });
      ws.dataValidations.add(`J2:J${lastData}`, {
        type: "list",
        allowBlank: true,
        formulae: ["Lists!$D$1:$D$2"],
      });
      ws.dataValidations.add(`AN2:AN${lastData}`, {
        type: "list",
        allowBlank: true,
        formulae: ["Lists!$E$1:$E$2"],
      });
      ws.dataValidations.add(`AO2:AO${lastData}`, {
        type: "list",
        allowBlank: true,
        formulae: ["Lists!$F$1:$F$2"],
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `investment_declarations_template_${currentFy || payMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Declaration template downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to build template");
    }
  };

  const handleDeclarationImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canEditPayroll) {
      toast.error("You do not have permission to import declarations");
      e.target.value = "";
      return;
    }
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xls") && !lower.endsWith(".xlsx")) {
      toast.error("Only Excel files (.xls / .xlsx) are allowed");
      e.target.value = "";
      return;
    }
    setImportBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await payrollService.bulkImportDeclarations(formData);
      if (res.errors?.length) {
        toast.error(
          res.message ||
            `${res.imported || res.count || 0} imported, ${res.errors.length} error(s)`
        );
        console.warn("Declaration import errors", res.errors);
      } else {
        toast.success(
          res.message || `Imported ${res.imported || res.count || 0} declaration(s)`
        );
      }
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setImportBusy(false);
      if (declFileRef.current) declFileRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center gap-2 rounded-lg border py-12 text-sm ${
        darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
      }`}>
        <Loader2 size={18} className="animate-spin" />
        Loading TDS review…
      </div>
    );
  }

  if (!result) {
    return (
      <div className={`rounded-lg border border-dashed p-8 text-center ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}>
        <p className={`text-sm font-medium ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
          TDS Review / Audit
        </p>
        <p className={`mt-1 text-sm ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
          Click Validate to review new joinees (DOJ in this month) and TDS applied at Process for{" "}
          {payMonth}. Prefer Process Payroll first — this step is audit / override, not assumption-based TDS.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      <div
        className={`flex shrink-0 flex-wrap items-center gap-2 border-b pb-2 ${
          darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"
        }`}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
              activeTab === tab.id
                ? "bg-[hsl(var(--ds-primary))] text-white"
                : darkMode
                  ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            {tab.label}
            <span className="ml-1.5 tabular-nums opacity-80">({tab.count})</span>
            {tab.badge > 0 && tab.id === "tds" && (
              <span className="ml-1 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] text-white">
                {tab.badge} Δ
              </span>
            )}
          </button>
        ))}

        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
          <div className="w-72 min-w-[18rem] sm:w-80">
            <EmpCodeNameSearchBar
              value={tableSearch}
              onChange={setTableSearch}
              darkMode={darkMode}
              className="!flex-none w-full"
            />
          </div>

          {activeTab === "tds" ? (
            <>
              {canEditPayroll && (
                <>
                  <button
                    type="button"
                    onClick={downloadTdsOverrideTemplate}
                    disabled={importBusy}
                    className={toolbarBtn}
                  >
                    <Upload size={12} /> Template
                  </button>
                  <label
                    className={`${toolbarBtn} cursor-pointer ${importBusy ? "pointer-events-none opacity-50" : ""}`}
                  >
                    {importBusy ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Download size={12} />
                    )}
                    Import
                    <input
                      ref={tdsFileRef}
                      type="file"
                      accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      onChange={handleTdsImport}
                      disabled={importBusy}
                    />
                  </label>
                </>
              )}
              {canViewTdsDetail && (
                <button
                  type="button"
                  onClick={downloadTdsComputationExport}
                  disabled={!showAllTds || exportBusy || !payMonth}
                  className={toolbarBtn}
                  title={
                    showAllTds
                      ? `Export TDS computation for all FY ${fyLabel || ""} employees (Active + Joined + Relieved)`
                      : "Enable Show all employees to export"
                  }
                >
                  {exportBusy ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Download size={12} />
                  )}
                  Computation
                </button>
              )}
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                <input
                  type="checkbox"
                  checked={!!showAllTds}
                  onChange={(e) => onToggleShowAll?.(e.target.checked)}
                  className="rounded"
                />
                Show all employees
              </label>
              {canViewTdsDetail && (
                <button
                  type="button"
                  onClick={() => setOneTimeTaxOpen(true)}
                  disabled={!payMonth}
                  className={toolbarBtn}
                  title="Cross-check One Time tax Monthly Variables from payroll DB"
                >
                  Onetime Tax
                </button>
              )}
            </>
          ) : (
            <>
              {canEditPayroll && (
                <>
                  <button
                    type="button"
                    onClick={downloadDeclarationTemplate}
                    disabled={importBusy}
                    className={toolbarBtn}
                  >
                    <Upload size={12} /> Template
                  </button>
                  <label
                    className={`${toolbarBtn} cursor-pointer ${importBusy ? "pointer-events-none opacity-50" : ""}`}
                  >
                    {importBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    Import
                    <input
                      ref={declFileRef}
                      type="file"
                      accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      className="hidden"
                      onChange={handleDeclarationImport}
                      disabled={importBusy}
                    />
                  </label>
                </>
              )}
              {canViewTdsDetail && (
                <button
                  type="button"
                  onClick={downloadDeclarationsGridExcel}
                  disabled={!uniqueDeclarations.length}
                  className={toolbarBtn}
                  title="Export declarations grid with TDS Computed"
                >
                  <Download size={12} /> Computation
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {tdsError && (
        <div className="shrink-0 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          TDS engine error: {tdsError}. Restart the backend after pulling latest changes, then Validate
          again.
        </div>
      )}

      {activeTab === "declarations" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable
            columns={DECLARATION_COLUMNS}
            rows={uniqueDeclarations}
            darkMode={darkMode}
            onViewTaxPlanner={taxPlannerAction}
            emptyMessage="No new joinees for this payroll month."
            search={tableSearch}
            onSearchChange={setTableSearch}
            hideSearchBar
          />
        </div>
      )}

      {activeTab === "tds" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <DataTable
            columns={TDS_COLUMNS}
            rows={tdsRows}
            darkMode={darkMode}
            onViewComputation={canViewTdsDetail ? setComputationRow : null}
            onViewTaxPlanner={taxPlannerAction}
            onOverride={null}
            isEmployeeProcessed={isEmployeeProcessed}
            emptyMessage={
              tdsRowsAll.length
                ? "No TDS discrepancies. Check “Show all employees” to see everyone."
                : "No TDS data yet. Assign salary structures, then Validate."
            }
            search={tableSearch}
            onSearchChange={setTableSearch}
            hideSearchBar
          />
        </div>
      )}

      {oneTimeTaxOpen && (
        <OneTimeTaxModal
          payMonth={payMonth}
          darkMode={darkMode}
          onClose={() => setOneTimeTaxOpen(false)}
        />
      )}

      {overrideRow && (
        <OverrideModal
          row={overrideRow}
          payMonth={payMonth}
          darkMode={darkMode}
          locked={isEmployeeProcessed(Number(overrideRow.employeeId ?? overrideRow.id))}
          onClose={() => setOverrideRow(null)}
          onSaved={() => onRefresh?.()}
        />
      )}

      {computationRow && (
        <TdsComputationBreakdownModal
          row={computationRow}
          payMonth={payMonth}
          darkMode={darkMode}
          onClose={() => setComputationRow(null)}
        />
      )}

      {taxPlannerEmployee && (
        <Suspense
          fallback={
            <div className="pointer-events-none fixed inset-0 z-[100060] flex items-start justify-center pt-24">
              <div
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs shadow-lg ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
                }`}
              >
                <Loader2 size={14} className="animate-spin" /> Loading Tax Planning…
              </div>
            </div>
          }
        >
          <TaxPlanningModal
            employee={taxPlannerEmployee}
            mode={canEditPayroll ? "edit" : "view"}
            onClose={() => setTaxPlannerEmployee(null)}
            onSave={async (updatedData) => {
              if (
                updatedData?.type === "submitted" ||
                updatedData?.type === "submit_success" ||
                updatedData?.type === "section_saved"
              ) {
                if (typeof onRefresh === "function") {
                  try {
                    await onRefresh();
                  } catch {
                    /* ignore refresh errors */
                  }
                }
              }
              if (
                updatedData?.type === "submitted" ||
                updatedData?.type === "submit_success"
              ) {
                setTaxPlannerEmployee(null);
              }
            }}
            payrollSettings={payrollSettings}
            isInvestmentDeclarationEnabled={!!payrollSettings?.isInvestmentEnabled}
            isInvestmentProofEnabled={!!payrollSettings?.isProofEnabled}
            getInvestmentDeclarationMode={() =>
              payrollSettings?.investmentMode || "ALWAYS"
            }
            getInvestmentProofMode={() =>
              String(payrollSettings?.proofMode || "manual").toLowerCase()
            }
            financialYear={fyLabel}
            payMonth={payMonth}
            adminBypassWindows={!!canEditPayroll}
          />
        </Suspense>
      )}
    </div>
  );
}

export { OverrideModal };
